// ABOUTME: Authentication context providing user state and auth actions.
// ABOUTME: Wraps Auth0 functionality and provides mock auth for development.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenGetter } from '@/services/api';
import { setAdminTokenGetter } from '@/services/api/adminClient';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  workspace_id: string;
  role: 'admin' | 'user';
  emailVerified: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check if we're in development mode (mock auth enabled)
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true' ||
  !import.meta.env.VITE_AUTH0_DOMAIN;

console.log('[AuthContext] Module loaded');
console.log('[AuthContext] USE_MOCK_AUTH:', USE_MOCK_AUTH);
console.log('[AuthContext] VITE_USE_MOCK_AUTH:', import.meta.env.VITE_USE_MOCK_AUTH);
console.log('[AuthContext] VITE_AUTH0_DOMAIN:', import.meta.env.VITE_AUTH0_DOMAIN);

// Mock user for development
// IDs must match the MOCK_TOKEN payload for backend validation
const MOCK_USER: AuthUser = {
  id: '695f049fc5bd1005a5e53a5e', // Valid ObjectId matching token sub
  email: 'dev@obsidianbi.com',
  name: 'Development User',
  picture: undefined,
  workspace_id: '695f049fc5bd1005a5e53a5f', // Valid ObjectId matching token workspace_id
  role: 'admin',
  emailVerified: true,
};

// Mock token for development - Valid JWT signed with backend's dev secret key
// Workspace: 695f049fc5bd1005a5e53a5f (Odynn-workspace with data source)
// Expires: Jan 2027 (1 year from generation)
const MOCK_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhdXRoMHx1c2VyX2Rldl8wMDEiLCJlbWFpbCI6ImRldkB0ZXN0LmNvbSIsIndvcmtzcGFjZV9pZCI6IjY5NWYwNDlmYzViZDEwMDVhNWU1M2E1ZiIsInJvbGUiOiJhZG1pbiIsImlhdCI6MTc2NzkwMjczMCwiZXhwIjoxNzk5NDM4NzMwfQ.DL4YTCC7VEpw2ODHL6Zp0a_Ba69X69qM7lhQsOg3Xaw';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const auth0 = useAuth0();
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const renderCount = useRef(0);
  renderCount.current++;

  console.log('[AuthContext] AuthProvider render #', renderCount.current);
  console.log('[AuthContext] auth0 state:', {
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    user: auth0.user ? { sub: auth0.user.sub, email: auth0.user.email } : null,
    error: auth0.error?.message,
  });

  useEffect(() => {
    console.log('[AuthContext] useEffect triggered');
    console.log('[AuthContext] Dependencies:', {
      'auth0.isLoading': auth0.isLoading,
      'auth0.isAuthenticated': auth0.isAuthenticated,
      'auth0.user': auth0.user ? 'exists' : 'null',
    });

    const initAuth = async () => {
      console.log('[AuthContext] initAuth() called');
      try {
        if (USE_MOCK_AUTH) {
          console.log('[AuthContext] Using MOCK auth mode');
          // In mock mode, auto-login after a short delay
          await new Promise((resolve) => setTimeout(resolve, 500));
          setAuthUser(MOCK_USER);
          setIsLoading(false);
          console.log('[AuthContext] Mock auth complete, user set');
          return;
        }

        console.log('[AuthContext] Using REAL Auth0 mode');
        console.log('[AuthContext] auth0.isLoading:', auth0.isLoading);

        // Real Auth0 mode
        if (!auth0.isLoading) {
          console.log('[AuthContext] Auth0 finished loading');
          console.log('[AuthContext] auth0.isAuthenticated:', auth0.isAuthenticated);
          console.log('[AuthContext] auth0.user:', auth0.user);
          console.log('[AuthContext] auth0.error:', auth0.error);

          if (auth0.isAuthenticated && auth0.user) {
            console.log('[AuthContext] User is authenticated!');
            console.log('[AuthContext] Full auth0.user object:', JSON.stringify(auth0.user, null, 2));
            // Transform Auth0 user to our AuthUser format
            const user: AuthUser = {
              id: auth0.user.sub || '',
              email: auth0.user.email || '',
              name: auth0.user.name || '',
              picture: auth0.user.picture,
              // Read role and workspace from Auth0 custom claims (set via Auth0 Action)
              workspace_id: (auth0.user['https://oleum.pro/workspace_id'] as string) || 'ws_default',
              role: (auth0.user['https://oleum.pro/role'] as 'admin' | 'user') || 'user',
              // Standard OIDC claim from Auth0; defaults to true for backward compatibility
              emailVerified: auth0.user.email_verified ?? true,
            };
            console.log('[AuthContext] Transformed user:', user);

            // Redirect non-Oleum users away from staging to production
            if (
              window.location.hostname === 'staging.app.oleum.pro' &&
              !user.email.endsWith('@oleum.pro')
            ) {
              window.location.href = 'https://app.oleum.pro';
              return;
            }

            setAuthUser(user);
          } else {
            console.log('[AuthContext] NOT authenticated or no user');
            console.log('[AuthContext] auth0.isAuthenticated:', auth0.isAuthenticated);
            console.log('[AuthContext] auth0.user exists:', !!auth0.user);
            setAuthUser(null);
          }
          setIsLoading(false);
          console.log('[AuthContext] isLoading set to false');
        } else {
          console.log('[AuthContext] Auth0 still loading, waiting...');
        }
      } catch (err) {
        console.error('[AuthContext] Error in initAuth:', err);
        setError(err instanceof Error ? err : new Error('Auth initialization failed'));
        setIsLoading(false);
      }
    };

    initAuth();
  }, [auth0.isLoading, auth0.isAuthenticated, auth0.user]);

  const login = () => {
    if (USE_MOCK_AUTH) {
      setAuthUser(MOCK_USER);
    } else {
      auth0.loginWithRedirect();
    }
  };

  const logout = () => {
    if (USE_MOCK_AUTH) {
      setAuthUser(null);
      // Redirect to login page after logout
      window.location.href = '/login';
    } else {
      auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };

  const getAccessToken = async (): Promise<string> => {
    console.log('[AuthContext] getAccessToken() called');
    console.log('[AuthContext] USE_MOCK_AUTH:', USE_MOCK_AUTH);

    if (USE_MOCK_AUTH) {
      console.log('[AuthContext] Returning mock token');
      return MOCK_TOKEN;
    }

    try {
      console.log('[AuthContext] Calling auth0.getAccessTokenSilently()...');
      console.log('[AuthContext] Current auth0 state before getAccessTokenSilently:', {
        isLoading: auth0.isLoading,
        isAuthenticated: auth0.isAuthenticated,
        user: auth0.user ? 'exists' : 'null',
        error: auth0.error?.message,
      });

      const startTime = Date.now();
      const token = await auth0.getAccessTokenSilently({
        cacheMode: 'on',
        detailedResponse: false,
      });
      const elapsed = Date.now() - startTime;

      console.log('[AuthContext] getAccessTokenSilently succeeded in', elapsed, 'ms');
      console.log('[AuthContext] Token length:', token?.length);

      // Decode and log token info
      if (token) {
        try {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            const exp = new Date(payload.exp * 1000);
            const now = new Date();
            console.log('[AuthContext] Token payload:', {
              sub: payload.sub,
              aud: payload.aud,
              iss: payload.iss,
              exp: exp.toISOString(),
              iat: payload.iat ? new Date(payload.iat * 1000).toISOString() : 'N/A',
            });
            console.log('[AuthContext] Token expires in:', Math.round((exp.getTime() - now.getTime()) / 1000 / 60), 'minutes');
            console.log('[AuthContext] Token expired:', exp < now);
          }
        } catch (decodeErr) {
          console.log('[AuthContext] Could not decode token:', decodeErr);
        }
      }

      return token;
    } catch (err: unknown) {
      console.error('[AuthContext] getAccessTokenSilently FAILED');
      console.error('[AuthContext] Error:', err);

      // Log detailed error info
      if (err && typeof err === 'object') {
        const errorObj = err as Record<string, unknown>;
        console.error('[AuthContext] Error type:', errorObj.constructor?.name);
        console.error('[AuthContext] Error name:', errorObj.name);
        console.error('[AuthContext] Error message:', errorObj.message);
        console.error('[AuthContext] Error error:', errorObj.error);
        console.error('[AuthContext] Error error_description:', errorObj.error_description);
        console.error('[AuthContext] Full error object:', JSON.stringify(errorObj, null, 2));
      }

      // Check if it's an error that requires user interaction
      // Skip redirects on print pages (Playwright PDF generation uses token auth, not Auth0)
      const isOnPrintPage = window.location.pathname.includes('/print') &&
        new URLSearchParams(window.location.search).has('token');

      if (err && typeof err === 'object' && 'error' in err) {
        const authError = err as { error: string; error_description?: string };
        if (authError.error === 'login_required') {
          if (isOnPrintPage) {
            console.log('[AuthContext] Login required but on print page with token - skipping redirect');
            throw err;
          }
          // Check if we're on a public route — don't redirect for shared reports, pricing, etc.
          const publicPaths = ['/r/', '/pricing', '/reports/', '/login', '/auth/'];
          const isPublicRoute = publicPaths.some(p => window.location.pathname.startsWith(p));
          if (isPublicRoute) {
            console.log('[AuthContext] Login required on public route — not redirecting');
            throw new Error('Not authenticated');
          }
          console.error('[AuthContext] Login required - redirecting to login');
          auth0.loginWithRedirect();
          // Return a never-resolving promise since we're redirecting
          return new Promise(() => {});
        } else if (authError.error === 'consent_required') {
          if (isOnPrintPage) {
            console.log('[AuthContext] Consent required but on print page - skipping redirect');
            throw err;
          }
          console.error('[AuthContext] Consent required - redirecting to login with consent prompt');
          auth0.loginWithRedirect({
            authorizationParams: {
              prompt: 'consent',
            },
          });
          // Return a never-resolving promise since we're redirecting
          return new Promise(() => {});
        } else if (authError.error === 'invalid_grant') {
          if (isOnPrintPage) {
            console.log('[AuthContext] Invalid grant but on print page - skipping redirect');
            throw err;
          }
          console.error('[AuthContext] Invalid grant - refresh token expired, redirecting to login');
          auth0.loginWithRedirect();
          // Return a never-resolving promise since we're redirecting
          return new Promise(() => {});
        }
      }

      throw err;
    }
  };

  // Set up API client token getter synchronously on first render
  // This ensures it's available before any child components make API calls
  const tokenGetterSet = useRef(false);
  if (!tokenGetterSet.current) {
    setTokenGetter(getAccessToken);
    setAdminTokenGetter(getAccessToken); // Also set for admin API client
    tokenGetterSet.current = true;
  }

  const value: AuthContextType = {
    user: authUser,
    isAuthenticated: !!authUser,
    isLoading: USE_MOCK_AUTH ? isLoading : auth0.isLoading || isLoading,
    error: error || (auth0.error ? new Error(auth0.error.message) : null),
    login,
    logout,
    getAccessToken,
  };

  console.log('[AuthContext] Context value:', {
    user: value.user ? { id: value.user.id, email: value.user.email } : null,
    isAuthenticated: value.isAuthenticated,
    isLoading: value.isLoading,
    error: value.error?.message,
  });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
